/**
 * Painel de conexão das contas do Instagram e das Páginas do Facebook.
 *
 * O workspace pode ter várias contas, até o teto do plano. A unidade é a
 * Página do Facebook: o Instagram profissional só existe vinculado a uma, e os
 * dois chegam pelo mesmo login — por isso cada cartão aqui é "Página + o
 * Instagram dela", e conta como UMA no plano.
 *
 * Duas etapas de propósito ao conectar: primeiro descobrir as páginas, depois
 * escolher. Um login costuma alcançar várias páginas, e é aqui que aparece o
 * erro de configuração mais comum, que a Meta não explica: página SEM conta
 * profissional vinculada.
 *
 * O token de página nunca volta para o navegador: o servidor redescobre na hora
 * de salvar.
 */

import { useState } from "react";
import { useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import { usePermissions } from "@/hooks/usePermissions";
import { toUserFriendlyError } from "@/lib/error-messages";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Instagram,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Trash2,
  RefreshCw,
  Download,
  Search,
  Facebook,
  BookOpen,
  Plus,
} from "lucide-react";
import { toast } from "sonner";

type Conta = {
  id: number;
  igUserId: string;
  pageId: string | null;
  username: string | null;
  name: string | null;
  profilePicUrl: string | null;
  maskedToken: string;
  humanAgentEnabled: boolean;
  lastVerifiedAt: Date | string | null;
  lastVerifyStatus: string | null;
  lastVerifyError: string | null;
};

type Pagina = {
  id: number;
  pageId: string;
  name: string | null;
  pictureUrl: string | null;
};

/** O que vai para a tela: a Página com o Instagram dela (cada um pode faltar). */
type Conexao = { chave: string; pagina: Pagina | null; conta: Conta | null };

function montarConexoes(paginas: Pagina[], contas: Conta[]): Conexao[] {
  const usadas = new Set<number>();
  const out: Conexao[] = paginas.map((p) => {
    const conta = contas.find((c) => c.pageId === p.pageId) ?? null;
    if (conta) usadas.add(conta.id);
    return { chave: `p${p.id}`, pagina: p, conta };
  });
  // Conexões antigas, de antes de as Páginas serem guardadas à parte.
  for (const c of contas) if (!usadas.has(c.id)) out.push({ chave: `c${c.id}`, pagina: null, conta: c });
  return out;
}

export default function InstagramPanel() {
  const utils = trpc.useUtils();
  const { can } = usePermissions();
  const [, navigate] = useLocation();
  const podeGerenciar = can("instagram:manage_connection");

  const [token, setToken] = useState("");
  // OAuth escolhe VÁRIAS páginas de uma vez; o token colado, uma só.
  const [escolhidas, setEscolhidas] = useState<string[]>([]);
  const [desconectando, setDesconectando] = useState<Conexao | null>(null);

  const lista = trpc.instagram.account.list.useQuery(undefined, { enabled: podeGerenciar });

  const invalidar = () => {
    void utils.instagram.account.list.invalidate();
    void utils.instagram.account.get.invalidate();
    void utils.instagram.account.options.invalidate();
  };

  // Estado do Login do Facebook. Traz as páginas autorizadas quando a pessoa
  // acabou de voltar do diálogo — o token em si nunca chega aqui.
  const oauth = trpc.instagram.account.oauthState.useQuery(undefined, { enabled: podeGerenciar });

  const conectarOAuth = trpc.instagram.account.connectFromOAuth.useMutation({
    onSuccess: (r) => {
      setEscolhidas([]);
      invalidar();
      void utils.instagram.account.oauthState.invalidate();
      if (r.conectadas.length > 0) {
        toast.success(
          r.conectadas.length === 1
            ? "Conta conectada pelo Facebook."
            : `${r.conectadas.length} contas conectadas pelo Facebook.`,
        );
      }
      for (const e of r.erros) toast.error(`${e.pageName ?? e.pageId}: ${e.motivo}`);
    },
    onError: (err) => toast.error(toUserFriendlyError(err)),
  });

  const descobrir = trpc.instagram.account.discover.useMutation({
    onError: (err) => toast.error(toUserFriendlyError(err)),
  });

  const conectar = trpc.instagram.account.connect.useMutation({
    onSuccess: () => {
      setToken("");
      setEscolhidas([]);
      descobrir.reset();
      invalidar();
      toast.success("Instagram conectado.");
    },
    onError: (err) => toast.error(toUserFriendlyError(err)),
  });

  const desconectarConta = trpc.instagram.account.disconnect.useMutation({
    onSuccess: () => {
      invalidar();
      setDesconectando(null);
      toast.success("Conta desconectada.");
    },
    onError: (err) => toast.error(toUserFriendlyError(err)),
  });

  const desconectarPagina = trpc.instagram.account.disconnectPage.useMutation({
    onSuccess: () => {
      invalidar();
      setDesconectando(null);
      toast.success("Conta desconectada.");
    },
    onError: (err) => toast.error(toUserFriendlyError(err)),
  });

  if (!podeGerenciar) {
    return (
      <p className="text-sm text-muted-foreground">
        Você não tem permissão para gerenciar a conexão com o Instagram. Peça a um administrador do
        workspace.
      </p>
    );
  }

  const conexoes = montarConexoes(lista.data?.paginas ?? [], lista.data?.contas ?? []);
  const limite = lista.data?.limite;
  const noLimite = limite?.maximo != null && limite.usado >= limite.maximo;

  // As páginas vêm do diálogo do Facebook ou da busca por token colado. Os
  // dois caminhos produzem a MESMA lista; o que muda é qual mutation conclui
  // e se dá para escolher várias.
  const viaOAuth = !!oauth.data?.pendente && !descobrir.data;
  const paginas = viaOAuth ? (oauth.data?.paginas ?? []) : (descobrir.data ?? []);
  const jaConectada = (pageId: string) =>
    viaOAuth
      ? !!oauth.data?.paginas.find((p) => p.pageId === pageId)?.jaConectada
      : conexoes.some((c) => c.pagina?.pageId === pageId || c.conta?.pageId === pageId);
  // Pelo Facebook dá para conectar Página sem Instagram (para publicar no
  // Facebook); pelo token colado, que existe para o Instagram, não.
  const selecionavel = (p: { hasInstagram: boolean }) => viaOAuth || p.hasInstagram;
  const novas = escolhidas.filter((id) => !jaConectada(id)).length;
  const passaDoLimite = limite?.maximo != null && limite.usado + novas > limite.maximo;

  const alternar = (pageId: string) => {
    if (!viaOAuth) {
      setEscolhidas([pageId]);
      return;
    }
    setEscolhidas((atual) => (atual.includes(pageId) ? atual.filter((x) => x !== pageId) : [...atual, pageId]));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3">
        <div className="rounded-lg bg-pink-100 p-2 dark:bg-pink-900/40">
          <Instagram className="h-5 w-5 text-pink-700 dark:text-pink-300" />
        </div>
        <div>
          <h3 className="text-lg font-semibold">Instagram e Facebook</h3>
          <p className="text-sm text-muted-foreground">
            Receba as mensagens diretas no Atendimento e trabalhe os comentários dos posts de cada
            conta conectada.
          </p>
        </div>
        {limite && (
          <Badge
            variant="outline"
            className={`ml-auto shrink-0 ${noLimite ? "border-amber-400 text-amber-800 dark:text-amber-300" : ""}`}
            title="Cada Página do Facebook, com o Instagram vinculado a ela, conta como uma."
          >
            {limite.maximo == null
              ? `${limite.usado} ${limite.usado === 1 ? "conta" : "contas"}`
              : `${limite.usado} de ${limite.maximo} ${limite.maximo === 1 ? "conta" : "contas"} do plano`}
          </Badge>
        )}
      </div>

      {/* ── Contas conectadas ──────────────────────────────────────────────── */}
      {conexoes.map((cx) => (
        <CartaoConexao key={cx.chave} conexao={cx} onMudou={invalidar} onDesconectar={() => setDesconectando(cx)} />
      ))}

      {/* ── Conectar ───────────────────────────────────────────────────────── */}
      <Card>
        <CardContent className="space-y-4 pt-6">
          {noLimite && (
            <p className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
              Seu plano já está com todas as contas em uso. Para conectar outra, desconecte uma
              acima ou faça upgrade do plano. Reconectar uma conta que já está aqui continua
              liberado.
            </p>
          )}

          {oauth.data?.disponivel && (
            <div className="space-y-2">
              <Button
                className="w-full bg-[#1877F2] text-white hover:bg-[#166FE5]"
                onClick={() => {
                  // Navegação de página inteira, não fetch: OAuth é uma ida ao
                  // domínio da Meta e uma volta por redirect. XHR não sai daqui.
                  window.location.href = "/api/integrations/meta/connect?purpose=instagram";
                }}
              >
                {conexoes.length > 0 ? <Plus className="mr-2 h-4 w-4" /> : <Facebook className="mr-2 h-4 w-4" />}
                {conexoes.length > 0 ? "Conectar ou reconectar contas com o Facebook" : "Entrar com Facebook"}
              </Button>
              <p className="text-xs text-muted-foreground">
                Você escolhe as páginas na tela da Meta e volta para cá. Nenhuma senha passa pelo
                LeadConversa.
              </p>
            </div>
          )}

          {oauth.data?.disponivel && (
            <div className="flex items-center gap-3">
              <div className="h-px flex-1 bg-border" />
              <span className="text-xs text-muted-foreground">ou cole um token</span>
              <div className="h-px flex-1 bg-border" />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="ig-token">Token de acesso do Facebook</Label>
            <Input
              id="ig-token"
              type="password"
              autoComplete="off"
              placeholder="EAAG..."
              value={token}
              onChange={(e) => setToken(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Gere um token com as permissões <code>instagram_basic</code>,{" "}
              <code>instagram_manage_messages</code>, <code>instagram_manage_comments</code> e{" "}
              <code>pages_manage_metadata</code>. O token da página é descoberto pelo servidor e
              guardado criptografado — ele não passa pelo navegador.
            </p>
          </div>

          <Button
            variant="outline"
            onClick={() => {
              setEscolhidas([]);
              descobrir.mutate({ accessToken: token.trim() });
            }}
            disabled={token.trim().length < 20 || descobrir.isPending}
          >
            {descobrir.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Search className="mr-2 h-4 w-4" />
            )}
            Procurar contas
          </Button>

          {descobrir.isSuccess && paginas.length === 0 && (
            <p className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
              O token é válido, mas não alcança nenhuma página do Facebook. Confira se o usuário que
              gerou o token administra a página.
            </p>
          )}

          {paginas.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium">
                {viaOAuth ? "Escolha as contas que quer conectar:" : "Escolha a conta:"}
              </p>
              {paginas.map((p) => {
                const marcada = escolhidas.includes(p.pageId);
                const pode = selecionavel(p);
                return (
                  <button
                    key={p.pageId}
                    type="button"
                    disabled={!pode}
                    onClick={() => alternar(p.pageId)}
                    className={`w-full rounded border p-3 text-left transition-colors ${
                      marcada ? "border-primary bg-muted/50" : "hover:border-primary/50 hover:bg-muted/30"
                    } ${!pode ? "cursor-not-allowed opacity-60" : ""}`}
                  >
                    <div className="flex items-center gap-3">
                      {viaOAuth && <Checkbox checked={marcada} disabled={!pode} tabIndex={-1} aria-hidden />}
                      {(p.instagram?.profilePicUrl ?? p.pagePictureUrl) && (
                        <img
                          src={(p.instagram?.profilePicUrl ?? p.pagePictureUrl) as string}
                          alt=""
                          className="h-8 w-8 rounded-full object-cover"
                          loading="lazy"
                        />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">
                          {p.instagram?.username ? `@${p.instagram.username}` : p.pageName ?? p.pageId}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {p.hasInstagram
                            ? `Página: ${p.pageName ?? p.pageId}`
                            : viaOAuth
                              ? "Só Facebook — esta página não tem Instagram profissional vinculado"
                              : "Esta página não tem conta profissional do Instagram vinculada"}
                        </p>
                      </div>
                      {jaConectada(p.pageId) && (
                        <Badge variant="secondary" className="shrink-0">
                          Já conectada
                        </Badge>
                      )}
                      {p.instagram?.followersCount != null && (
                        <span className="shrink-0 text-xs text-muted-foreground">
                          {new Intl.NumberFormat("pt-BR").format(p.instagram.followersCount)} seguidores
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}

              {passaDoLimite && limite?.maximo != null && (
                <p className="text-xs text-amber-700 dark:text-amber-300">
                  Seu plano tem {limite.maximo} {limite.maximo === 1 ? "conta" : "contas"} e{" "}
                  {limite.usado} já {limite.usado === 1 ? "está" : "estão"} em uso — as que passarem do
                  limite não serão conectadas.
                </p>
              )}

              <Button
                onClick={() => {
                  if (escolhidas.length === 0) return;
                  if (viaOAuth) conectarOAuth.mutate({ pageIds: escolhidas });
                  else conectar.mutate({ accessToken: token.trim(), pageId: escolhidas[0] });
                }}
                disabled={escolhidas.length === 0 || conectar.isPending || conectarOAuth.isPending}
              >
                {(conectar.isPending || conectarOAuth.isPending) && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {escolhidas.length > 1 ? `Conectar ${escolhidas.length} contas` : "Conectar esta conta"}
              </Button>
            </div>
          )}

          {/* O manual é aberto DAQUI porque é aqui que a pessoa trava: a
              parte difícil da conexão acontece no app do Instagram e no
              Gerenciador de Negócios, fora desta tela. */}
          <button
            type="button"
            onClick={() => navigate("/conectar-meta")}
            className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline"
          >
            <BookOpen className="h-3.5 w-3.5" />
            Ver o passo a passo completo
          </button>

          <div className="rounded border bg-muted/30 p-3 text-xs text-muted-foreground">
            <p className="font-medium text-foreground">Falta configurar o webhook na Meta</p>
            <p className="mt-1">
              No painel do app, produto Instagram → Webhooks, aponte para{" "}
              <code>{window.location.origin}/api/webhooks/instagram</code> e assine os campos{" "}
              <code>messages</code>, <code>comments</code> e <code>mentions</code>. Sem isso as
              mensagens não chegam aqui.
            </p>
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={!!desconectando} onOpenChange={(v) => !v && setDesconectando(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Desconectar{" "}
              {desconectando?.conta?.username
                ? `@${desconectando.conta.username}`
                : (desconectando?.pagina?.name ?? "esta conta")}
              ?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {desconectando?.conta
                ? "As mensagens diretas desta conta deixam de chegar no Atendimento e os comentários param de entrar na fila. "
                : ""}
              {desconectando?.pagina ? "A Página do Facebook também sai da lista. " : ""}
              As conversas e os comentários já recebidos continuam aqui — nada é apagado, e nada muda
              no Instagram ou no Facebook.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => {
                if (!desconectando) return;
                if (desconectando.pagina) desconectarPagina.mutate({ id: desconectando.pagina.id });
                else if (desconectando.conta) desconectarConta.mutate({ id: desconectando.conta.id });
              }}
            >
              Desconectar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function CartaoConexao({
  conexao,
  onMudou,
  onDesconectar,
}: {
  conexao: Conexao;
  onMudou: () => void;
  onDesconectar: () => void;
}) {
  const { conta: c, pagina } = conexao;

  const testar = trpc.instagram.account.test.useMutation({
    onSuccess: (r) => {
      onMudou();
      toast.success(`Conexão ok — @${r.perfil.username ?? "conta"}.`);
    },
    onError: (err) => {
      onMudou();
      toast.error(toUserFriendlyError(err));
    },
  });

  const importar = trpc.instagram.account.importConversations.useMutation({
    onSuccess: (r) => {
      toast.success(
        r.mensagens > 0
          ? `${r.conversas} conversa(s) e ${r.mensagens} mensagem(ns) trazidas para o Atendimento.`
          : `Nada novo: as ${r.conversas} conversa(s) recentes já estavam no Atendimento.`,
      );
    },
    onError: (err) => toast.error(toUserFriendlyError(err)),
  });

  const atualizar = trpc.instagram.account.update.useMutation({
    onSuccess: () => {
      onMudou();
      toast.success("Configuração salva.");
    },
    onError: (err) => toast.error(toUserFriendlyError(err)),
  });

  const foto = c?.profilePicUrl ?? pagina?.pictureUrl ?? null;
  const ocupado = testar.isPending || importar.isPending;

  return (
    <Card>
      <CardContent className="space-y-3 pt-6">
        <div className="flex flex-wrap items-center gap-3">
          {foto && <img src={foto} alt="" className="h-9 w-9 rounded-full object-cover" loading="lazy" />}
          <div className="min-w-0">
            <p className="font-medium">{c ? (c.name ?? c.username ?? c.igUserId) : (pagina?.name ?? pagina?.pageId)}</p>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
              {c?.username && (
                <span className="inline-flex items-center gap-1">
                  <Instagram className="h-3 w-3" />@{c.username}
                </span>
              )}
              {pagina && (
                <span className="inline-flex items-center gap-1">
                  <Facebook className="h-3 w-3" />
                  {pagina.name ?? pagina.pageId}
                </span>
              )}
              {!c && <span>Sem Instagram vinculado</span>}
            </div>
          </div>
          {c && <code className="ml-auto rounded bg-muted px-2 py-1 text-xs">{c.maskedToken}</code>}
        </div>

        {/* Token de página pode ser revogado no Business Manager sem aviso
            nenhum para cá. Esta linha é o único sinal antes de uma DM ficar
            sem resposta. */}
        {c && (
          <div className="flex items-center gap-2 text-sm">
            {c.lastVerifyStatus === "ok" ? (
              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
            ) : c.lastVerifyStatus === "failed" ? (
              <AlertCircle className="h-4 w-4 text-red-600" />
            ) : null}
            <span className="text-muted-foreground">
              {c.lastVerifiedAt
                ? `Última verificação: ${new Date(c.lastVerifiedAt).toLocaleString("pt-BR")}`
                : "Ainda não verificado"}
            </span>
          </div>
        )}
        {c?.lastVerifyStatus === "failed" && c.lastVerifyError && (
          <p className="rounded border border-red-200 bg-red-50 p-2 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
            {c.lastVerifyError}
          </p>
        )}

        {c && (
          <div className="flex items-start justify-between gap-4 rounded border p-3">
            <div className="min-w-0">
              <Label htmlFor={`ig-human-agent-${c.id}`} className="text-sm font-medium">
                Janela estendida de atendimento (7 dias)
              </Label>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Por padrão a Meta só aceita resposta livre até 24h depois da mensagem da pessoa. Com a
                permissão de atendimento humano aprovada no App Review, o prazo sobe para 7 dias.
                Ligue apenas se a aprovação saiu — senão a Meta recusa o envio.
              </p>
            </div>
            <Switch
              id={`ig-human-agent-${c.id}`}
              checked={c.humanAgentEnabled}
              onCheckedChange={(v) => atualizar.mutate({ id: c.id, humanAgentEnabled: v })}
              disabled={atualizar.isPending}
            />
          </div>
        )}

        <div className="flex flex-wrap gap-2 pt-1">
          {c && (
            <Button variant="outline" size="sm" onClick={() => testar.mutate({ id: c.id })} disabled={ocupado}>
              {testar.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              Testar conexão
            </Button>
          )}
          {c && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => importar.mutate({ limit: 50, accountId: c.id })}
              disabled={ocupado}
              title="Traz as conversas que já existiam no Direct antes de conectar. Pode repetir: nada duplica."
            >
              {importar.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              Importar conversas recentes
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            className="text-red-600 hover:text-red-700"
            onClick={onDesconectar}
            disabled={ocupado}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Desconectar
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
